import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calculator, RotateCcw, Video, Image as ImageIcon, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) =>
  `$${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const SUGGESTED_DISCOUNTS = [25, 40, 50];

const DiscountPresets = ({ value, onChange, testIdPrefix }) => (
  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
    <span className="text-[10px] text-gray-500 mr-1">Sugeridos:</span>
    {SUGGESTED_DISCOUNTS.map((p) => {
      const active = Number(value) === p;
      return (
        <button
          key={p}
          type="button"
          data-testid={`${testIdPrefix}-preset-${p}`}
          onClick={() => onChange(p)}
          className={`text-xs px-2 py-0.5 rounded-full border transition-all ${
            active
              ? "bg-purple-500/30 border-purple-400 text-purple-200"
              : "bg-zinc-800 border-zinc-700 text-gray-400 hover:border-purple-500/50 hover:text-purple-300"
          }`}
        >
          {p}%
        </button>
      );
    })}
  </div>
);

/**
 * Calculadora de estimación de contenido custom.
 * - Chatter elige una modelo de un dropdown.
 * - Se autodetectan: precio_custom_video (por minuto), precio_custom_photo (por foto),
 *   precio_videollamada (sesión fija hasta 10 min).
 * - 3 líneas independientes (video / fotos / videollamada). Cada una con su propio descuento %.
 * - Devuelve un total y permite copiar una cotización formateada para WhatsApp.
 */
export default function CustomCalculator() {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("");
  const [loading, setLoading] = useState(true);

  // Video custom (por minuto)
  const [videoQty, setVideoQty] = useState(0);       // cantidad de videos
  const [videoMin, setVideoMin] = useState(0);       // minutos por video
  const [videoDisc, setVideoDisc] = useState(0);     // % descuento

  // Fotos (por unidad)
  const [photoQty, setPhotoQty] = useState(0);
  const [photoDisc, setPhotoDisc] = useState(0);

  // Videollamada (sesión fija hasta 10 min)
  const [callQty, setCallQty] = useState(0);
  const [callDisc, setCallDisc] = useState(0);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      // Intentamos /models y como fallback chatter-view (mismas fields de precio).
      const tryEndpoints = ["/models", "/models/chatter-view", "/models/list"];
      for (const path of tryEndpoints) {
        try {
          const res = await axios.get(`${API}${path}`, { withCredentials: true });
          if (mounted && Array.isArray(res.data) && res.data.length > 0) {
            // Sólo activas
            const list = res.data
              .filter((m) => (m.status || "activa") !== "inactiva")
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setModels(list);
            break;
          }
        } catch (e) {
          // probar siguiente
        }
      }
      if (mounted) setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const model = useMemo(
    () => models.find((m) => m.id === modelId) || null,
    [models, modelId]
  );

  // Tarifas detectadas (default 0 si la modelo no tiene)
  const rateVideoPerMin = Number(model?.precio_custom_video || 0);
  const ratePhoto = Number(model?.precio_custom_photo || 0);
  const rateCall = Number(model?.precio_videollamada || 0);

  // Subtotales y totales por línea
  const videoSubtotal = rateVideoPerMin * (Number(videoQty) || 0) * (Number(videoMin) || 0);
  const videoTotal = videoSubtotal * (1 - Math.min(Math.max(Number(videoDisc) || 0, 0), 100) / 100);

  const photoSubtotal = ratePhoto * (Number(photoQty) || 0);
  const photoTotal = photoSubtotal * (1 - Math.min(Math.max(Number(photoDisc) || 0, 0), 100) / 100);

  const callSubtotal = rateCall * (Number(callQty) || 0);
  const callTotal = callSubtotal * (1 - Math.min(Math.max(Number(callDisc) || 0, 0), 100) / 100);

  const grandTotal = videoTotal + photoTotal + callTotal;
  const grandSubtotal = videoSubtotal + photoSubtotal + callSubtotal;
  const grandSavings = Math.max(grandSubtotal - grandTotal, 0);

  const reset = () => {
    setVideoQty(0);
    setVideoMin(0);
    setVideoDisc(0);
    setPhotoQty(0);
    setPhotoDisc(0);
    setCallQty(0);
    setCallDisc(0);
  };

  return (
    <Card
      data-testid="custom-calculator"
      className="bg-[#0a0a0a] border border-purple-500/30 rounded-2xl"
    >
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <Calculator className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-white text-xl">Calculadora de Contenido Custom</CardTitle>
            <p className="text-xs text-gray-400 mt-0.5">
              Selecciona una modelo y arma una cotización en segundos
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Nota de sugerencia */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-amber-400 text-base shrink-0">💡</span>
            <div className="text-amber-100/90">
              <span className="font-semibold text-amber-300">Tip de cierre:</span>{" "}
              Los descuentos sugeridos son <strong className="text-amber-300">25%, 40% y 50%</strong>.{" "}
              Calcula el valor del descuento según el{" "}
              <em className="text-amber-200">spending power</em> del cliente — qué tanto suele
              gastar, hace cuánto compra y qué tan persuadido está. No regales: descuento alto solo
              para cerrar al instante.
            </div>
          </div>
        </div>

        {/* Selector de modelo */}
        <div>
          <Label className="text-gray-300 text-sm">Modelo</Label>
          <Select value={modelId} onValueChange={setModelId}>
            <SelectTrigger
              data-testid="calc-model-select"
              className="bg-zinc-800 border-zinc-700 text-white mt-1"
            >
              <SelectValue placeholder={loading ? "Cargando…" : "Selecciona una modelo"} />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
              {models.map((m) => (
                <SelectItem key={m.id} value={m.id} className="text-white focus:bg-zinc-800">
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {model && (
          <>
            {/* Tarifas detectadas */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-zinc-800/50 border border-purple-500/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-purple-300 text-xs">
                  <Video className="w-4 h-4" /> Video Custom
                </div>
                <div className="text-xl font-bold text-white mt-1">
                  {rateVideoPerMin > 0 ? `${fmtMoney(rateVideoPerMin)}/min` : "—"}
                </div>
              </div>
              <div className="bg-zinc-800/50 border border-pink-500/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-pink-300 text-xs">
                  <ImageIcon className="w-4 h-4" /> Foto Custom
                </div>
                <div className="text-xl font-bold text-white mt-1">
                  {ratePhoto > 0 ? `${fmtMoney(ratePhoto)}/foto` : "—"}
                </div>
              </div>
              <div className="bg-zinc-800/50 border border-cyan-500/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-cyan-300 text-xs">
                  <Phone className="w-4 h-4" /> Videollamada
                </div>
                <div className="text-xl font-bold text-white mt-1">
                  {rateCall > 0 ? `${fmtMoney(rateCall)}/sesión` : "—"}
                </div>
                <div className="text-[10px] text-gray-500">hasta 10 min</div>
              </div>
            </div>

            {/* Línea 1: Videos */}
            <div className="bg-zinc-900/50 border border-purple-500/20 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-purple-300 font-semibold">
                  <Video className="w-4 h-4" /> Videos Custom
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-gray-500">Subtotal {fmtMoney(videoSubtotal)}</div>
                  <div className="text-emerald-400 font-bold">{fmtMoney(videoTotal)}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-gray-400 text-xs">Cantidad</Label>
                  <Input
                    data-testid="calc-video-qty"
                    type="number"
                    min="0"
                    value={videoQty}
                    onChange={(e) => setVideoQty(parseInt(e.target.value) || 0)}
                    className="bg-zinc-800 border-zinc-700 text-white h-9"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Min/video</Label>
                  <Input
                    data-testid="calc-video-min"
                    type="number"
                    min="0"
                    value={videoMin}
                    onChange={(e) => setVideoMin(parseFloat(e.target.value) || 0)}
                    className="bg-zinc-800 border-zinc-700 text-white h-9"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Descuento %</Label>
                  <Input
                    data-testid="calc-video-disc"
                    type="number"
                    min="0"
                    max="100"
                    value={videoDisc}
                    onChange={(e) =>
                      setVideoDisc(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))
                    }
                    className="bg-zinc-800 border-zinc-700 text-white h-9"
                  />
                  <DiscountPresets value={videoDisc} onChange={setVideoDisc} testIdPrefix="calc-video-disc" />
                </div>
              </div>
            </div>

            {/* Línea 2: Fotos */}
            <div className="bg-zinc-900/50 border border-pink-500/20 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-pink-300 font-semibold">
                  <ImageIcon className="w-4 h-4" /> Fotos Custom
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-gray-500">Subtotal {fmtMoney(photoSubtotal)}</div>
                  <div className="text-emerald-400 font-bold">{fmtMoney(photoTotal)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-gray-400 text-xs">Cantidad fotos</Label>
                  <Input
                    data-testid="calc-photo-qty"
                    type="number"
                    min="0"
                    value={photoQty}
                    onChange={(e) => setPhotoQty(parseInt(e.target.value) || 0)}
                    className="bg-zinc-800 border-zinc-700 text-white h-9"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Descuento %</Label>
                  <Input
                    data-testid="calc-photo-disc"
                    type="number"
                    min="0"
                    max="100"
                    value={photoDisc}
                    onChange={(e) =>
                      setPhotoDisc(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))
                    }
                    className="bg-zinc-800 border-zinc-700 text-white h-9"
                  />
                  <DiscountPresets value={photoDisc} onChange={setPhotoDisc} testIdPrefix="calc-photo-disc" />
                </div>
              </div>
            </div>

            {/* Línea 3: Videollamadas */}
            <div className="bg-zinc-900/50 border border-cyan-500/20 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-cyan-300 font-semibold">
                  <Phone className="w-4 h-4" /> Videollamadas <span className="text-xs text-gray-500">(máx 10 min)</span>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-gray-500">Subtotal {fmtMoney(callSubtotal)}</div>
                  <div className="text-emerald-400 font-bold">{fmtMoney(callTotal)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-gray-400 text-xs">Cantidad sesiones</Label>
                  <Input
                    data-testid="calc-call-qty"
                    type="number"
                    min="0"
                    value={callQty}
                    onChange={(e) => setCallQty(parseInt(e.target.value) || 0)}
                    className="bg-zinc-800 border-zinc-700 text-white h-9"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Descuento %</Label>
                  <Input
                    data-testid="calc-call-disc"
                    type="number"
                    min="0"
                    max="100"
                    value={callDisc}
                    onChange={(e) =>
                      setCallDisc(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))
                    }
                    className="bg-zinc-800 border-zinc-700 text-white h-9"
                  />
                  <DiscountPresets value={callDisc} onChange={setCallDisc} testIdPrefix="calc-call-disc" />
                </div>
              </div>
            </div>

            {/* Total */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/40 rounded-xl p-5 flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-400">TOTAL ESTIMADO</div>
                {grandSavings > 0 && (
                  <div className="text-[11px] text-emerald-300">
                    🎁 Ahorro: {fmtMoney(grandSavings)} ({Math.round((grandSavings / grandSubtotal) * 100)}% off)
                  </div>
                )}
              </div>
              <div
                data-testid="calc-total"
                className="text-3xl sm:text-4xl font-bold text-emerald-400"
              >
                {fmtMoney(grandTotal)}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="calc-reset-btn"
                onClick={reset}
                variant="ghost"
                className="text-gray-400 hover:text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Resetear
              </Button>
            </div>
          </>
        )}

        {!model && !loading && (
          <div className="text-center text-gray-500 text-sm py-6">
            Selecciona una modelo para empezar la cotización
          </div>
        )}
      </CardContent>
    </Card>
  );
}
